import {
  LeanCustomsService,
  type LeanCustomsDeclaration,
} from "./leancustomsService";
import {
  leanCustomsOrderMatchesBooking,
  type LeanCustomsOrderRefSlice,
} from "../utils/bookingUtils";
import {
  buildLeanCustomsDeclarationSearchTerms,
  collectDeclarationIdsFromLeanPayload,
  mergeDeclarationsById,
  pickDeclarationsForBooking,
  searchTermsFromLeanCustomsOrder,
} from "../utils/leanCustomsDeclarationLookup";
import { extractCollectionResults } from "../utils/leanCustomsRecordUtils";
import { filterDeclarationsForCompanyOrg } from "../utils/leanCustomsDeclarationAccess";

export type LeanCustomsOrderRow = Record<string, unknown> & { id?: string };

function mergeOrdersById(lists: LeanCustomsOrderRow[][]): LeanCustomsOrderRow[] {
  const map = new Map<string, LeanCustomsOrderRow>();
  for (const list of lists) {
    for (const row of list) {
      const id = typeof row.id === "string" ? row.id : "";
      if (id) map.set(id, row);
    }
  }
  return [...map.values()];
}

async function fetchDeclarationsByIds(
  companyId: number,
  declarationIds: string[]
): Promise<LeanCustomsDeclaration[]> {
  const unique = [...new Set(declarationIds.map((id) => id.trim()).filter(Boolean))];
  if (unique.length === 0) return [];

  const fetched = await Promise.all(
    unique.map(async (id) => {
      const res = await LeanCustomsService.getDeclaration(id, companyId);
      return res.success && res.data ? res.data : null;
    })
  );
  return fetched.filter((d): d is LeanCustomsDeclaration => Boolean(d?.id));
}

/**
 * When an order is already linked, also resolve declarations via that order
 * (shipments / nested refs) and broaden `q` with the order's own reference.
 * Dexwork list-search by EFF alone often misses declarations whose primary
 * reference is "AWB / EFF…".
 */
async function declarationsFromLinkedOrder(
  companyId: number,
  linkedOrderId: string
): Promise<{ declarations: LeanCustomsDeclaration[]; extraSearchTerms: string[] }> {
  const orderId = linkedOrderId.trim();
  if (!orderId) return { declarations: [], extraSearchTerms: [] };

  const [orderRes, shipmentsRes] = await Promise.all([
    LeanCustomsService.getOrder(orderId, companyId),
    LeanCustomsService.listOrderShipments(orderId, companyId),
  ]);

  const order = orderRes.success ? orderRes.data ?? null : null;
  const extraSearchTerms = searchTermsFromLeanCustomsOrder(order);

  const ids = new Set<string>([
    ...collectDeclarationIdsFromLeanPayload(order),
    ...collectDeclarationIdsFromLeanPayload(shipmentsRes.success ? shipmentsRes.data : null),
    ...collectDeclarationIdsFromLeanPayload(extractCollectionResults(shipmentsRes.data)),
  ]);

  const declarations = await fetchDeclarationsByIds(companyId, [...ids]);
  return { declarations, extraSearchTerms };
}

export async function searchMatchingLeanCustomsOrders(
  organizationId: string,
  companyId: number,
  bookingId: number,
  bookingReference: string
): Promise<LeanCustomsOrderRow[]> {
  const listResults: LeanCustomsOrderRow[] = [];
  for (const q of buildLeanCustomsDeclarationSearchTerms(bookingReference, bookingId)) {
    const res = await LeanCustomsService.listOrders(organizationId, companyId, {
      page: 1,
      page_size: 100,
      q,
    });
    if (res.success && res.data?.results?.length) {
      listResults.push(...res.data.results);
    }
  }
  return mergeOrdersById([listResults]).filter((o) =>
    leanCustomsOrderMatchesBooking(o as LeanCustomsOrderRefSlice, bookingId)
  );
}

export async function searchMatchingLeanCustomsDeclarations(
  organizationId: string,
  companyId: number,
  bookingId: number,
  bookingReference: string,
  savedDeclarationId?: string | null,
  linkedOrderId?: string | null
): Promise<LeanCustomsDeclaration[]> {
  const fromOrder = linkedOrderId?.trim()
    ? await declarationsFromLinkedOrder(companyId, linkedOrderId)
    : { declarations: [], extraSearchTerms: [] as string[] };

  const searchTerms = buildLeanCustomsDeclarationSearchTerms(
    bookingReference,
    bookingId,
    fromOrder.extraSearchTerms
  );

  const listResults: LeanCustomsDeclaration[] = [];
  const linkedOrder = linkedOrderId?.trim() || undefined;

  for (const q of searchTerms) {
    const res = await LeanCustomsService.listDeclarations(organizationId, companyId, {
      page: 1,
      page_size: 100,
      q,
      ...(linkedOrder ? { order_id: linkedOrder } : {}),
    });
    if (res.success && res.data?.results?.length) {
      listResults.push(...res.data.results);
    }
  }

  let candidates = mergeDeclarationsById([listResults, fromOrder.declarations]);

  if (savedDeclarationId && !candidates.some((d) => d.id === savedDeclarationId)) {
    const savedRes = await LeanCustomsService.getDeclaration(
      savedDeclarationId,
      companyId
    );
    if (savedRes.success && savedRes.data) {
      candidates = mergeDeclarationsById([candidates, [savedRes.data]]);
    }
  }

  // Keep anything resolved via the linked order even if list `q` matching is noisy;
  // still require booking EFF match for pure search hits.
  const bookingMatched = pickDeclarationsForBooking(candidates, bookingId);
  return filterDeclarationsForCompanyOrg(
    mergeDeclarationsById([bookingMatched, fromOrder.declarations]),
    organizationId
  );
}
